import DesignToolsPanel from './DesignToolsPanel.vue';
import ShapeEditPanel from './ShapeEditPanel.vue';
import RulersOverlay from './RulersOverlay.vue';
import { t } from './i18n';
import { isShapeId, renderShape, type ShapeDesignItem } from './shapes';
import { rulersVisible, toggleRulers } from './rulerState';
import { fetchPublicSettings } from './settingsApi';
import { hasPrintableSize } from './rulerMath';

type RenderResult = { component: unknown; props?: Record<string, unknown> };

type VeyopFacade = {
  hooks?: {
    on?: (event: string, handler: (payload?: unknown) => void) => unknown;
  };
  designer?: {
    registerLeftPanel?: (entry: unknown) => unknown;
    registerOverlay?: (entry: unknown) => unknown;
    getContext?: () => { activeView: string };
  };
  panels?: {
    register?: (entry: unknown) => unknown;
  };
  itemBounds?: {
    register?: (entry: unknown) => unknown;
  };
  ui?: {
    registerToolbarAction?: (entry: unknown) => unknown;
  };
};

let registered = false;

/** Per-view "Printable size (cm)" lives in the server-injected designer config. */
function viewParams(): Record<string, unknown> | null {
  try {
    const config = JSON.parse(document.getElementById('designer-config')?.textContent || '{}');
    return config?.items?.params ?? null;
  } catch {
    return null;
  }
}

function isShapeItem(item: ShapeDesignItem | null | undefined): boolean {
  return item?.type === 'svg' && isShapeId(item.shapeId);
}

function pluginPublicPath(): string {
  const configEl = document.getElementById('designer-config');
  try {
    const config = JSON.parse(configEl?.textContent || '{}');
    const root = String(config.veyopUrl || '').replace(/\/$/, '');
    if (root) return `${root}/plugins/design-tools/dist`;
  } catch {
    // Fall through to script sniffing.
  }

  const script = Array.from(document.scripts).find((node) => node.src.includes('/plugins/design-tools/'));
  if (script?.src) {
    const marker = '/plugins/design-tools/';
    return `${script.src.slice(0, script.src.indexOf(marker))}/plugins/design-tools/dist`;
  }
  const base = (window as Window & { VeyopHostConfig?: { assetsUrl?: string } }).VeyopHostConfig?.assetsUrl || window.location.origin;
  return `${base.replace(/\/$/, '')}/plugins/design-tools/dist`;
}

function registerOnVeyop(Veyop: VeyopFacade): void {
  const designer = Veyop.designer;
  const registerLeftPanel = designer?.registerLeftPanel;
  if (registered || !registerLeftPanel) return;
  registered = true;
  const publicPath = pluginPublicPath();

  registerLeftPanel({
    id: 'design-tools',
    group: 'graphics',
    label: t('Tools'),
    description: t('Add shapes and emoji'),
    icon: {
      type: 'material',
      path: 'M600-360ZM320-242q10 1 19.5 1.5t20.5.5q11 0 20.5-.5T400-242v82h400v-400h-82q1-10 1.5-19.5t.5-20.5q0-11-.5-20.5T718-640h82q33 0 56.5 23.5T880-560v400q0 33-23.5 56.5T800-80H400q-33 0-56.5-23.5T320-160v-82ZM161.5-401.5Q80-483 80-600t81.5-198.5Q243-880 360-880t198.5 81.5Q640-717 640-600t-81.5 198.5Q477-320 360-320t-198.5-81.5Zm340-57Q560-517 560-600t-58.5-141.5Q443-800 360-800t-141.5 58.5Q160-683 160-600t58.5 141.5Q277-400 360-400t141.5-58.5ZM360-600Z',
    },
    priority: 38,
    render: (): RenderResult => ({ component: DesignToolsPanel, props: { publicPath } }),
  });

  Veyop.panels?.register?.({
    id: 'design-tools-shape',
    label: t('Shape'),
    description: t('Stroke and corners'),
    priority: 30,
    matches: (item: ShapeDesignItem | null) => isShapeItem(item),
    render: (ctx: { item: ShapeDesignItem | null; update: (patch: Record<string, unknown>) => void }): RenderResult => ({
      component: ShapeEditPanel,
      props: { item: ctx.item, update: ctx.update },
    }),
  });

  // Shapes are drawn for their exact box; redraw after a resize so the outline keeps hugging it.
  Veyop.itemBounds?.register?.({
    id: 'design-tools-shape',
    priority: 30,
    matches: (item: ShapeDesignItem) => isShapeItem(item),
    freeAspect: true,
    measure: (item: ShapeDesignItem) => ({ width: Number(item.width) || 1, height: Number(item.height) || 1 }),
    fitToBox: (item: ShapeDesignItem, box: { width: number; height: number }) => {
      const rendered = renderShape(item.shapeId, item.shapeParams ?? {}, item.svgColors ?? {}, box.width, box.height);
      return { shapeSourceSvg: rendered.source, svg: rendered.svg, src: rendered.src };
    },
  });

  void fetchPublicSettings().then((settings) => {
    if (!settings.rulers_enabled) return;
    const params = viewParams();

    designer.registerOverlay?.({
      id: 'design-tools-rulers',
      priority: 30,
      render: (ctx: Record<string, unknown>): RenderResult => ({ component: RulersOverlay, props: { context: ctx } }),
    });

    Veyop.ui?.registerToolbarAction?.({
      id: 'design-tools-rulers',
      label: t('Rulers (cm)'),
      icon: 'M20 5H5v15M16 5v3M12 5v5M8 5v3M5 9h3M5 13h5M5 17h3',
      priority: 50,
      // Without a printable size there is nothing to measure; that is an admin setup issue, not a shopper one.
      visible: () => hasPrintableSize(params, designer.getContext?.().activeView ?? ''),
      active: () => rulersVisible.value,
      onSelect: () => toggleRulers(),
    });
  });
}

function boot(): void {
  const w = window as unknown as { Veyop?: VeyopFacade };
  const tryRegister = () => {
    if (w.Veyop) registerOnVeyop(w.Veyop);
  };

  w.Veyop?.hooks?.on?.('veyop:designer:ready', tryRegister);
  tryRegister();

  const start = Date.now();
  const tick = () => {
    tryRegister();
    if (registered || Date.now() - start > 5000) return;
    setTimeout(tick, 50);
  };
  tick();
}

boot();
